import { useEffect, useState, useContext, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  InputAdornment,
  useTheme,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { AuthContext } from "../../context/AuthContext";
import {
  getAllUsers,
  createUserApi,
  updateUserApi,
  resetPasswordApi,
  assignRolesApi,
} from "../../api/users.api";
import { Plus } from "lucide-react";
import UserFormModal from "./UserFormModal";
import ResetPasswordModal from "./ResetPasswordModal";
import UsersTable from "./UsersTable";
import { getAllRolesApi } from "../../api/roles.api";
import AccessDenied from "../AccessDenied";
import PageTitle from "../../components/PageTitle";

export default function Users() {
  const {
    permissions = [],
    user: currentUser,
    loading: authLoading,
  } = useContext(AuthContext);

  const canView = permissions.includes("management.view");
  const canCreate = permissions.includes("management.create");
  const canUpdate = permissions.includes("management.update");
  const canDelete = permissions.includes("management.delete");

  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await getAllUsers();
      if (res?.data?.success === false) {
        setApiError(res.data.message || "Access denied");
        setUsers([]);
        setRolesList([]);
        return;
      }
      const userData = res.data.data || [];
      setUsers(userData);

      const r = await getAllRolesApi();
      setRolesList(r?.data?.data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Something went wrong";
      if (msg?.includes("Access Denied")) setApiError(msg);
      else setApiError("Something went wrong");
      setUsers([]);
      setRolesList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (permissions && !authLoading) fetchData();
  }, [permissions, authLoading]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase().trim();
    return users.filter(
      (u) =>
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.team && u.team.toLowerCase().includes(q)) ||
        (Array.isArray(u.roles) &&
          u.roles.some((r) =>
            (typeof r === "string" ? r : r.name || "")
              .toLowerCase()
              .includes(q)
          ))
    );
  }, [users, search]);

  // 🔥 ONLY TEAM FIX IS HERE (LOCAL STATE UPDATE)
  const handleSave = async (form) => {
    try {
      const { roles, team, ...payload } = form;
      let userId;

      if (editingUser) {
        // UPDATE USER
        await updateUserApi(editingUser._id, { ...payload, team });
        userId = editingUser._id;

        if (roles?.length > 0) {
          await assignRolesApi({ userId, roles: [roles[0]] });
        }
      } else {
        // CREATE USER
        const res = await createUserApi({ ...payload, team });
        const newUser = res.data?.data;
        userId = newUser?._id;

        if (userId && roles?.length > 0) {
          await assignRolesApi({ userId, roles: [roles[0]] });
        }
      }

      // Refresh data from server to get updated roles
      await fetchData();

      setOpenModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error("Error saving user:", err);
    }
  };

  const handleResetPassword = async (password) => {
    if (!resetUser) return;
    await resetPasswordApi(resetUser._id, { password });
    setOpenReset(false);
    setResetUser(null);
  };

  if (!authLoading && !canView) return <AccessDenied message={apiError} />;

  return (
    <Fade in={mounted} timeout={500}>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          "@keyframes slideUp": {
            from: { opacity: 0, transform: "translateY(12px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "slideUp 0.45s cubic-bezier(0.23, 1, 0.32, 1) forwards",
        }}
      >
        <Box
          mb={3}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
          }}
        >
          <PageTitle parts={["Users"]} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <TextField
              size="small"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "#9CA3AF", ml: 0.5 }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 240, md: 280 },
                bgcolor: "#fff",
                borderRadius: 3,
                transition: "box-shadow 0.25s ease",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: "1.5px",
                  },
                },
                "&:hover": {
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}18`,
                },
              }}
            />

            {canCreate && (
              <Button
                onClick={() => {
                  setEditingUser(null);
                  setOpenModal(true);
                }}
                startIcon={<Plus size={16} />}
                sx={{
                  borderRadius: 3,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.2 },
                  fontSize: { xs: 13, sm: 14 },
                  fontWeight: 700,
                  textTransform: "none",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                  color: "#fff",
                  boxShadow: `0 4px 14px ${theme.palette.primary.main}30`,
                  width: { xs: "100%", sm: "auto" },
                  transition: "all 0.25s cubic-bezier(0.23, 1, 0.32, 1)",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add User
              </Button>
            )}
          </Stack>
        </Box>

        <UsersTable
          users={filteredUsers}
          totalCount={users.length}
          loading={loading}
          canUpdate={canUpdate}
          canDelete={canDelete}
          canCreate={canCreate}
          currentUser={currentUser}
          onEdit={(u) => {
            setEditingUser(u);
            setOpenModal(true);
          }}
          onReset={(u) => {
            setResetUser(u);
            setOpenReset(true);
          }}
          onClearSearch={() => setSearch("")}
        />

        <UserFormModal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setEditingUser(null);
          }}
          user={editingUser}
          onSave={handleSave}
          rolesList={rolesList}
        />

        <ResetPasswordModal
          open={openReset}
          onClose={() => {
            setOpenReset(false);
            setResetUser(null);
          }}
          onSubmit={handleResetPassword}
        />
      </Box>
    </Fade>
  );
}
